/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Inviter, Registerer, SessionState, UserAgent } from 'sip.js';
import disableMic from '../../assets/icons/disable_mic.svg';
import mutedMicActive from '../../assets/icons/muted_mic_active.svg';
import mutedMic from '../../assets/icons/off_mic.svg';
import onlineMic from '../../assets/icons/online_mic.svg';
import stopIcon from '../../assets/icons/stop_icon.svg';

import { PHONE_REGISTER_STATUS, PHONE_STATES, } from '../../constants/system.keys';
import './index.scss';

const DEFAULT_SIP_PORT = 7443;

export const Mic = ({ speakerUsername, disable }) => {
  const remoteStream = new MediaStream();

  const [phoneState, setPhoneState] = useState(PHONE_STATES.NOT_SET);

  const [isMuted, setIsMuted] = useState(false);

  const [registerStatus, setRegisterStatus] = useState(
    PHONE_REGISTER_STATUS.UNSET
  );
  const [userAgent, setUserAgent] = useState(null);
  const [register, setRegister] = useState(null);

  const [outCallSession, setOutCallSession] = useState(null);
  const [isCalling, setIsCalling] = useState(false);

  const { sipAccount } = useSelector((state) => state);

  const audioRef = useRef();

  const setupRemoteMedia = (session) => {
    session.sessionDescriptionHandler.peerConnection
      .getReceivers()
      .forEach((receiver) => {
        if (receiver.track) {
          remoteStream.addTrack(receiver.track);
        }
      });

    if (audioRef.current) {
      audioRef.current.srcObject = remoteStream;
      audioRef.current.play();
    }
  };

  const cleanupMedia = () => {
    if (audioRef.current) {
      audioRef.current.srcObject = null;
      audioRef.current.pause();
    }
  };

  const terminatedCall = () => {
    if (outCallSession) {
      if (outCallSession.state === SessionState.Established) {
        outCallSession.bye();
      } else {
        outCallSession.cancel();
      }
    }
  };

  const clearRegisterCall = () => {
    terminatedCall();
    if (register) {
      register.unregister();
    }

    if (userAgent) {
      userAgent.stop();
    }

    setRegister(null);
    setUserAgent(null);
    setOutCallSession(null);
  };

  const registerSipCall = () => {
    if (sipAccount) {
      setRegisterStatus(PHONE_REGISTER_STATUS.REGISTERING);

      const uaConfig = {
        displayName: sipAccount.username,
        uri: UserAgent.makeURI(
          `sip:${sipAccount.username}@${sipAccount.endpoint}:${
            sipAccount.port || DEFAULT_SIP_PORT
          }`
        ),

        transportOptions: {
          server: `wss://${sipAccount.endpoint}:${
            sipAccount.port || DEFAULT_SIP_PORT
          }`,
          connectionTimeout: 100,
          keepAliveInterval: 20,
        },
        authorizationPassword: sipAccount.password,
        authorizationUsername: sipAccount.username,
        delegate: {
          onConnect: () => {
            console.log('====> onConnect');
            setRegisterStatus(PHONE_REGISTER_STATUS.REGISTERING);
          },
          onRegister: (r) => {
            console.log('====> onRegister');
          },
          onDisconnect: () => {
            console.log('====> onDisconnect');
          },
          onInvite: (invitation) => {
            console.log('====> Incoming call');
          },
        },
      };

      const ua = new UserAgent(uaConfig);
      const registerer = new Registerer(ua);

      // Add listen state change
      ua.transport.onConnect = () => {
        console.log('====> User Agent onConnect');
      };

      ua.transport.onDisconnect = () => {
        console.log('====> User Agent onDisconnect');
      };

      ua.start().then(() => {
        registerer.register({
          requestDelegate: {
            onAccept: (resp) => {
              console.log('Register is accepted: ', resp.message);
              setRegisterStatus(PHONE_REGISTER_STATUS.READY);
            },
            onReject: (response) => {
              console.log(' Register is rejected ===> ', response.message);
              setRegisterStatus(PHONE_REGISTER_STATUS.UNSET);
            },
          },
        });
      });

      setUserAgent(ua);
      setRegister(registerer);
    }
  };

  const changeMuteMic = (s) => {
    if (outCallSession) {
      const pc = outCallSession.sessionDescriptionHandler.peerConnection;
      pc.getLocalStreams().forEach((stream) => {
        stream.getAudioTracks().forEach(function (track) {
          track.enabled = !s;
        });
      });

      setIsMuted((prevState) => !prevState);
    }
  };

  const makeCall = () => {
    if (!userAgent) {
      registerSipCall();
      return;
    }

    const target = UserAgent.makeURI(
      `sip:${speakerUsername}@${sipAccount.endpoint}:${DEFAULT_SIP_PORT}`
    );

    if (!target) {
      console.error('Failed to create target URI.');
      return;
    }

    const inviter = new Inviter(userAgent, target, {
      sessionDescriptionHandlerOptions: {
        constraints: {
          audio: true,
          video: false,
        },
      },
    });

    inviter.stateChange.addListener((newState) => {
      switch (newState) {
        case SessionState.Establishing:
          console.log('==> Ringing call');
          setPhoneState(PHONE_STATES.RINGING);
          setIsCalling(true);
          break;
        case SessionState.Established:
          console.log('===> Call Established');
          setPhoneState(PHONE_STATES.IN_CALL);
          setIsCalling(false);
          setupRemoteMedia(inviter);
          break;

        case SessionState.Terminated:
          console.log('===> Call Terminated');
          setPhoneState(PHONE_STATES.TERMINATED);
          setIsCalling(false);
          cleanupMedia();
          break;
        default:
          setPhoneState(PHONE_STATES.NOT_SET);
          break;
      }
    });

    inviter
      .invite()
      .then((res) => {
        setPhoneState(PHONE_STATES.CALLING_OUT);
        setIsCalling(true);
        console.log(`Calling out: ${inviter.remoteIdentity.uri.user}`);
      })
      .catch((err) => {
        console.log('Call has rejected: ', err);
        setPhoneState(PHONE_STATES.REJECTED);
        setIsCalling(false);
      });

    setOutCallSession(inviter);
  };

  useEffect(() => {
    return () => {
      clearRegisterCall();
    };
  }, []);

  const getMicIcon = () => {
    if (disable || isCalling) {
      return <img alt="disable mic" src={disableMic} />;
    }

    if (phoneState === PHONE_STATES.IN_CALL) {
      return (
        <>
          <img
            className="speaker-icon"
            src={isMuted ? mutedMicActive : mutedMic}
            alt="mute mic"
            onClick={() => changeMuteMic(!isMuted)}
            style={{ marginRight: '5px' }}
          />
          <img
            className="speaker-icon"
            src={stopIcon}
            alt="stop call"
            onClick={terminatedCall}
          />
        </>
      );
    }

    return (
      <img
        className="speaker-icon"
        src={onlineMic}
        alt="mic online"
        onClick={makeCall}
      />
    );
  };

  return (
    <span>
      <audio ref={audioRef} autoPlay={true}></audio>
      {getMicIcon()}
    </span>
  );
};

export default Mic;
